import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Globe, Server, Zap, Shield, Activity, Layers, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const RESI = "https://images.unsplash.com/photo-1644088379091-d574269d422f";
const ISP = "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg";

const FAQ = [
  ["What's the difference between Residential and ISP proxies?", "Residential proxies route through real consumer ISPs and rotate on demand — the cleanest fingerprint for scraping at scale. ISP proxies are static IPs hosted on residential ASNs, giving you datacenter speed with residential trust."],
  ["How does pricing & billing work?", "Residential is a one-time purchase at $6/GB and never expires. ISP proxies are a monthly subscription at $2/IP per month (minimum 25 IPs) — you keep the exact same IPs on every renewal and can cancel anytime."],
  ["Do you support sticky and rotating sessions?", "Yes. Every residential proxy supports both sticky sessions (same IP for a duration) and rotating sessions (new IP per request) — configured per request via your credentials."],
  ["Which auth methods do you support?", "Sign in with email + password, Google, or Discord. Proxies themselves use username:password authentication."],
];

function Stat({ v, l }) {
  return (
    <div>
      <div className="font-head text-3xl font-extrabold text-ink">{v}</div>
      <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest mt-1">{l}</div>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen text-ink">
      <Navbar />

      <section className="relative pt-40 pb-28 overflow-hidden grain grid-bg">
        <div className="absolute inset-0 -z-10">
          <img src={HERO} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-base/40 via-base/70 to-base dark:from-[#05090A]/40 dark:via-[#05090A]/70 dark:to-[#05090A]" />
        </div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 border border-neon/30 rounded-full px-4 py-1.5 text-xs font-mono text-neon mb-8">
              <span className="w-2 h-2 rounded-full bg-neon animate-pulse" /> 99.9% UPTIME · REAL-TIME PROVISIONING
            </div>
            <h1 className="font-head text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
              Proxies built to<br /><span className="text-neon">win the drop.</span>
            </h1>
            <p className="mt-8 text-lg text-zinc-600 leading-relaxed max-w-xl">
              Millions of clean residential IPs and static ISP endpoints — battle-tested against Akamai, DataDome and Cloudflare. Generate and scale in seconds.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register"><Button className="bg-neon text-base hover:bg-neon-hover font-semibold h-12 px-8 text-base" data-testid="hero-get-started-btn">Get Started <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/pricing"><Button variant="outline" className="border-[#CBEEF5] bg-transparent text-ink hover:border-neon hover:text-neon h-12 px-8 text-base" data-testid="hero-pricing-btn">View Pricing</Button></Link>
            </div>
            <div className="mt-16 flex gap-12">
              <Stat v="150M+" l="Residential IPs" />
              <Stat v="<5ms" l="ISP Latency" />
              <Stat v="195" l="Countries" />
            </div>
          </div>
        </div>
      </section>

      <section id="products" className="max-w-7xl mx-auto px-6 py-24">
        <p className="font-mono text-xs text-neon tracking-[0.3em] uppercase mb-3">// The Network</p>
        <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight mb-14">Both networks. Battle tested.</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { img: RESI, tag: "RESIDENTIAL", icon: Globe, title: "Rotating & Sticky Residential", desc: "Real consumer IPs across every major carrier. Rotate on demand or hold sticky sessions — the cleanest fingerprint for scraping at scale.", price: "$6 / GB", accent: "text-neon" },
            { img: ISP, tag: "ISP", icon: Server, title: "Dedicated Static ISP", desc: "Dedicated static ISP IPs in Ashburn, Virginia — unlimited bandwidth, never rotating, always warm. Billed monthly, and you keep the same IPs on every renewal.", price: "$2 / IP / mo", accent: "text-lime" },
          ].map((c) => (
            <div key={c.tag} className="group relative bg-surface border border-[#CBEEF5] rounded-xl overflow-hidden hover:border-neon/50 transition-colors">
              <div className="h-48 overflow-hidden relative">
                <img src={c.img} alt={c.tag} className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent dark:from-[#0E1719]" />
                <span className={`absolute top-4 left-4 font-mono text-xs tracking-widest ${c.accent} border border-current/30 rounded px-2 py-1`}>{c.tag}</span>
              </div>
              <div className="p-8">
                <c.icon className={`w-6 h-6 ${c.accent} mb-4`} aria-hidden="true" />
                <h3 className="font-head text-2xl font-semibold mb-3">{c.title}</h3>
                <p className="text-zinc-600 leading-relaxed mb-6">{c.desc}</p>
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-xl ${c.accent}`}>{c.price}</span>
                  <Link to="/pricing"><Button variant="outline" className="border-[#CBEEF5] bg-transparent text-ink hover:border-neon hover:text-neon">Shop <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-4">
          {[[Zap, "Instant Delivery", "Provisioned in seconds via API"], [Shield, "Anti-bot Ready", "Akamai · DataDome · Cloudflare"], [Activity, "Live Usage", "Track bandwidth in real time"], [Layers, "No Expiry", "Purchased data never expires"]].map(([Icon, t, d]) => (
            <div key={t} className="bg-surface border border-[#CBEEF5] rounded-lg p-6 hover:border-neon/40 transition-colors">
              <Icon className="w-5 h-5 text-neon mb-3" aria-hidden="true" />
              <div className="font-head font-semibold mb-1">{t}</div>
              <div className="text-sm text-zinc-500">{d}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="faq" className="max-w-3xl mx-auto px-6 py-24">
        <p className="font-mono text-xs text-neon tracking-[0.3em] uppercase mb-3">// FAQ</p>
        <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight mb-10">Questions, answered.</h2>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map(([q, a]) => (
            <AccordionItem key={q} value={q} className="border-[#E2F5F9]">
              <AccordionTrigger className="text-left font-head hover:text-neon">{q}</AccordionTrigger>
              <AccordionContent className="text-zinc-600 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <footer className="border-t border-[#E2F5F9]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-neon" aria-hidden="true" />
            <span className="font-head font-extrabold tracking-tight">FR <span className="text-neon">PROXIES</span></span>
          </div>
          <p className="text-sm text-zinc-600 font-mono">© 2026 FR Proxies · Fanatikz Resale. All systems operational.</p>
        </div>
      </footer>
    </div>
  );
}
